import { defineStore } from 'pinia'
import { ElNotification } from 'element-plus'

import { PaginationParams } from '@/models/api'
import {
  EvCarProfile,
  EvCarProfileStatus,
  EvCarProfileUpdateableKeys,
  EvCarProfileUpdateParams,
  EvCarImage,
} from '@/models/evCarProfile'

import evCarsApi from '@/api/car-ev-data'

import { parseErrors } from '@/helpers/error-parsers'
import { commonCase } from '@/helpers/strings'

import getInitialState, {
  getInitialEvCarDetailsState,
  getInitialEvCarTripsState,
  EvCarsState,
  GetEvCarsParams,
} from './state'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapApiResponseToEvCarProfile(data: any): EvCarProfile {
  const parseNumberField = (value: any) => {
    if (value === null || value === undefined) return 0
    return typeof value === 'string' ? parseFloat(value) : value
  }

  return {
    id: data.id ?? '',
    admin_id: data.admin_id ?? '',
    owner_ev_car: data.owner_ev_car ?? null,
    place_id: data.place_id ?? null,
    address: data.address ?? null,
    model_make_id: data.model_make_id ?? '',
    model_id: data.model_id ?? null,
    model_name: data.model_name ?? '',
    model_year: data.model_year ?? 0,
    battery_capacity: parseNumberField(data.battery_capacity),
    charging_connector_type: data.charging_connector_type ?? '',
    charging_time: parseNumberField(data.charging_time),
    range_per_charge: parseNumberField(data.range_per_charge),
    price_per_minute_ev: parseNumberField(data.price_per_minute_ev),
    price_per_km_ev: parseNumberField(data.price_per_km_ev),
    price_per_minute_up_to_100_km_ev: parseNumberField(
      data.price_per_minute_up_to_100_km_ev
    ),
    price_per_minute_above_100_km_ev: parseNumberField(
      data.price_per_minute_above_100_km_ev
    ),
    price_per_hour_up_to_100_km_ev: parseNumberField(
      data.price_per_hour_up_to_100_km_ev
    ),
    price_per_hour_above_100_km_ev: parseNumberField(
      data.price_per_hour_above_100_km_ev
    ),
    price_per_hour_ev: parseNumberField(data.price_per_hour_ev),
    pause_price_ev: parseNumberField(data.pause_price_ev),
    deposit_amount_ev: parseNumberField(data.deposit_amount_ev),
    start_price: parseNumberField(data.start_price),
    reserve_price_ev: parseNumberField(data.reserve_price_ev),
    reservation_extension_price: parseNumberField(
      data.reservation_extension_price
    ),
    car_type: data.car_type ?? '',
    transmission: data.transmission ?? '',
    fuel_type: data.fuel_type ?? '',
    no_branded_title: data.no_branded_title ?? false,
    identity_country: data.identity_country ?? '',
    plate_number: data.plate_number ?? '',
    seats: data.seats ?? null,
    doors: data.doors ?? null,
    status: data.status
      ? typeof data.status === 'string'
        ? (data.status as EvCarProfileStatus)
        : EvCarProfileStatus.INCOMPLETE
      : EvCarProfileStatus.INCOMPLETE,
    car_description: data.car_description ?? null,
    charging_included: data.charging_included ?? false,
    battery_included: data.battery_included ?? false,
    charging_policy: data.charging_policy ?? null,
    features: data.features ?? {
      gps: data.gps ?? null,
      air_conditioner: data.air_conditioner ?? null,
      bluetooth_stereo: data.bluetooth_stereo ?? null,
      wifi_internet: data.wifi_internet ?? null,
      fire_extinguisher: data.fire_extinguisher ?? null,
    },
    images: data.images ?? [],
    createdAt: data.createdAt ?? '',
    updatedAt: data.updatedAt ?? '',
    status_doors: data.status_doors ?? false,
    status_engine: data.status_engine ?? false,
    status_windows: data.status_windows ?? false,
    type_connection_geotek: data.type_connection_geotek ?? '',
    activate_key_ev_car: data.activate_key_ev_car ?? false,
    assigned_user_id: data.assigned_user_id ?? null,
    assigned_from: data.assigned_from ?? null,
    assigned_until: data.assigned_until ?? null,
    assigned_user: data.assigned_user ?? null,

    // Location and geolocation fields
    location: {
      lat: parseNumberField(data.location?.lat) ?? 0,
      lng: parseNumberField(data.location?.lng) ?? 0,
    },
    locality: data.locality ?? null,
    location_geom: data.location_geom
      ? {
          type: data.location_geom.type ?? 'Point',
          coordinates: data.location_geom.coordinates ?? [0, 0],
        }
      : null,

    // Battery and range related fields
    range_per_full_charge: parseNumberField(data.range_per_full_charge),
    electric_level: parseNumberField(data.electric_level),
    battery_details: data.battery_details ?? {},
    supported_charging_types: data.supported_charging_types ?? [],

    // Vehicle status and metrics
    odometer_indicators: parseNumberField(data.odometer_indicators),
    remaining_distance: parseNumberField(data.remaining_distance),
    engine_status: data.engine_status ?? false,

    // Reservation and limits
    max_distance_per_tariff: parseNumberField(data.max_distance_per_tariff),
    max_distance_per_minute_tariff: parseNumberField(
      data.max_distance_per_minute_tariff
    ),
    max_distance_per_hour_tariff: parseNumberField(
      data.max_distance_per_hour_tariff
    ),
    reservation_timer: parseNumberField(data.reservation_timer),
    reservation_status: data.reservation_status ?? '',
    pause_limit: parseNumberField(data.pause_limit),
    pause_usage_limit: data.pause_usage_limit ?? false,

    // Additional car details
    no_smoking: data.no_smoking ?? false,
    current_location: data.current_location ?? '',
    registered_car: data.registered_car ?? false,
    external_id: data.external_id ?? '',
    registration_status: data.registration_status ?? '',
    min_age_requirement: parseNumberField(data.min_age_requirement),
    advance_notice: data.advance_notice ?? '',
    rating:
      data.rating !== undefined && data.rating !== null
        ? String(data.rating)
        : '3',

    // Documents
    ownership_permit: data.ownership_permit ?? [],

    // Direct wifi property
    wifi_internet: data.wifi_internet ?? false,

    // Admin comments history
    admin_comment: Array.isArray(data.admin_comment)
      ? data.admin_comment
      : typeof data.admin_comment === 'string'
      ? (() => {
          try {
            return JSON.parse(data.admin_comment)
          } catch {
            return []
          }
        })()
      : [],
  }
}

export const useEvCarsStore = defineStore({
  id: 'evCars-store',
  state: getInitialState,
  actions: {
    async deleteEvCarImage(carId: string, imageId: string) {
      try {
        await evCarsApi.deleteEvCarImage(carId, imageId)

        this.$patch((state: EvCarsState) => {
          const carDetails = state.evCarsDetails[carId]
          if (carDetails?.data?.images) {
            carDetails.data.images = carDetails.data.images.filter(
              image => image.id !== imageId
            )
          }
        })

        ElNotification({
          type: 'success',
          message: 'Image successfully deleted.',
          duration: 6000,
        })
      } catch (error) {
        const { error: parsedError } = parseErrors(error)

        ElNotification({
          type: 'error',
          message: parsedError || 'Failed to delete image.',
          duration: 10000,
        })

        throw error
      }
    },

    async uploadImg(carId: string, file: File, type: string) {
      try {
        if (!carId) {
          throw new Error('Car ID is not defined.')
        }

        const response = await evCarsApi.uploadEvCarImage(carId, file, type)

        const uploadedImage = response.data.data as EvCarImage

        if (!uploadedImage) {
          throw new Error(
            `Uploaded image data for type "${type}" not found in response.`
          )
        }

        this.$patch((state: EvCarsState) => {
          const carDetails = state.evCarsDetails[carId]
          if (carDetails?.data?.images) {
            carDetails.data.images.push(uploadedImage)
          } else if (carDetails?.data) {
            carDetails.data.images = [uploadedImage]
          }
        })

        ElNotification({
          type: 'success',
          message: `${type} image uploaded successfully.`,
          duration: 6000,
        })
        return uploadedImage
      } catch (error) {
        const { error: parsedError } = parseErrors(error)

        ElNotification({
          type: 'error',
          message: parsedError || 'Failed to upload image.',
          duration: 10000,
        })

        throw error
      }
    },

    async getEvCars(params: PaginationParams & GetEvCarsParams) {
      try {
        this.$patch({ listModule: { isLoading: true, error: null } })

        const response = await evCarsApi.getEvCarProfiles(params)

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let data: any = response.data.data
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let metaData: any = response.data.meta

        // Handle nested data structure: { data: { data: [], meta: {} } }
        if (!Array.isArray(data) && data?.data && Array.isArray(data.data)) {
          metaData = data.meta
          data = data.data
        }

        const list: EvCarProfile[] = (Array.isArray(data) ? data : []).map(
          mapApiResponseToEvCarProfile
        )

        const meta = metaData
          ? {
              total: metaData.total,
              perPage:
                metaData.per_page || metaData.perPage || params.perPage || 10,
              currentPage:
                metaData.current_page ||
                metaData.currentPage ||
                params.page ||
                1,
              lastPage: metaData.last_page || metaData.lastPage || 1,
              firstPage: metaData.first_page || metaData.firstPage || '',
              firstPageUrl:
                metaData.first_page_url || metaData.firstPageUrl || '',
              lastPageUrl: metaData.last_page_url || metaData.lastPageUrl || '',
              nextPageUrl: metaData.next_page_url || metaData.nextPageUrl || '',
              previousPageUrl:
                metaData.previous_page_url || metaData.previousPageUrl || '',
            }
          : {
              total: 0,
              perPage: params.perPage || 10,
              currentPage: params.page || 1,
              lastPage: 1,
              firstPage: '',
              firstPageUrl: '',
              lastPageUrl: '',
              nextPageUrl: '',
              previousPageUrl: '',
            }

        this.$patch({
          listModule: {
            list,
            meta,
            isLoading: false,
            isInitialLoadingDone: true,
          },
        })
      } catch (e) {
        console.error('Failed to fetch EV Cars:', e)
        this.$patch({
          listModule: {
            error: parseErrors(e).error,
            isLoading: false,
            isInitialLoadingDone: true,
          },
        })
      }
    },

    async getEvCarDetails(id: string) {
      try {
        const evCarsDetails = this.evCarsDetails

        if (!evCarsDetails[id]) {
          evCarsDetails[id] = getInitialEvCarDetailsState()
        }

        this.$patch({
          evCarsDetails: { [id]: { isLoading: true, error: null } },
        })

        const response = await evCarsApi.getEvCarProfileById(id)
        const data = response.data.data

        this.$patch({
          evCarsDetails: {
            [id]: {
              isLoading: false,
              data: mapApiResponseToEvCarProfile(data),
            },
          },
        })
      } catch (e) {
        const { error } = parseErrors(e)
        this.$patch({
          evCarsDetails: { [id]: { isLoading: false, error } },
        })
      }
    },

    async updateEvCarStatus(
      id: string,
      status: EvCarProfileStatus,
      options?: { admin_comment?: string }
    ) {
      try {
        if (!this.evCarsDetails[id]?.data) {
          ElNotification({
            type: 'error',
            message: 'EV Car data is not available for update.',
            duration: 6000,
          })
          return
        }

        const updateParams: EvCarProfileUpdateParams = { status }
        if (options?.admin_comment) {
          updateParams.admin_comment = options.admin_comment
        }

        await this.updateEvVehicle(id, updateParams)

        ElNotification({
          type: 'success',
          duration: 6000,
          message: 'EV Car status was successfully updated.',
        })
      } catch (e) {
        const { error } = parseErrors(e)
        ElNotification({ message: error, duration: 10000, type: 'error' })
        throw e // Re-throw to handle in component
      }
    },

    async updateEvVehicle(
      id: string,
      params: EvCarProfileUpdateParams | FormData
    ) {
      const paramsObj =
        params instanceof FormData ? Object.fromEntries(params) : params

      const updatedKeys =
        params instanceof FormData
          ? (Array.from(params.keys()) as EvCarProfileUpdateableKeys[])
          : (Object.keys(params) as EvCarProfileUpdateableKeys[])

      const keysString = updatedKeys
        .map(key => commonCase(key, true))
        .join(', ')

      try {
        if (!this.evCarsDetails[id]?.data) {
          throw new Error('EV Car data must be received before updating')
        }

        this.$patch((state: EvCarsState) => {
          const updatingModule = state.evCarsDetails[id]?.updating
          if (!updatingModule) return

          updatingModule.error = null
          updatedKeys.forEach(key => {
            if (!updatingModule.loadingKeys.includes(key)) {
              updatingModule.loadingKeys.push(key)
            }
            const successIndex = updatingModule.successKeys.indexOf(key)
            if (successIndex !== -1) {
              updatingModule.successKeys.splice(successIndex, 1)
            }
          })
        })

        const { data: updatedVehicle } = await evCarsApi.updateEvCarProfile(
          id,
          params
        )

        if (!updatedVehicle?.data) {
          throw new Error('Failed to fetch updated vehicle data')
        }

        const processedVehicle = mapApiResponseToEvCarProfile(
          updatedVehicle.data
        )

        this.$patch((state: EvCarsState) => {
          const detailsModule = state.evCarsDetails[id]
          if (!detailsModule?.data) return

          const oldValue = detailsModule.data.price_per_hour_above_100_km_ev

          detailsModule.data = {
            ...detailsModule.data,
            ...processedVehicle,
          }

          const evCarInList = state.listModule.list.find(item => item.id === id)
          if (evCarInList) {
            Object.assign(evCarInList, processedVehicle)
          }

          const updatingModule = detailsModule.updating
          if (updatingModule) {
            updatedKeys.forEach(key => {
              if (!updatingModule.successKeys.includes(key)) {
                updatingModule.successKeys.push(key)
              }
              const loadingIndex = updatingModule.loadingKeys.indexOf(key)
              if (loadingIndex !== -1) {
                updatingModule.loadingKeys.splice(loadingIndex, 1)
              }
            })
          }
        })

        ElNotification({
          type: 'success',
          duration: 6000,
          message: `${keysString} successfully updated.`,
        })
      } catch (e) {
        const { error, validationErrors } = parseErrors(e)

        this.$patch({
          evCarsDetails: {
            [id]: {
              updating: {
                error: validationErrors || error,
                loadingKeys: [],
                successKeys: [],
              },
            },
          },
        })

        ElNotification({
          message: `Error updating ${keysString}: ${error}`,
          type: 'error',
          duration: 10000,
        })
      }
    },

    async setEvCarAssignedUser(id: string, userId: string | null) {
      try {
        const { data: response } = await evCarsApi.setEvCarAssignedUser(
          id,
          userId
        )
        const updated = mapApiResponseToEvCarProfile(response.data)
        const patch = {
          status: updated.status,
          admin_comment: updated.admin_comment,
          assigned_user_id: updated.assigned_user_id,
          assigned_from: updated.assigned_from,
          assigned_until: updated.assigned_until,
          assigned_user: updated.assigned_user,
        }

        this.$patch((state: EvCarsState) => {
          const detailsModule = state.evCarsDetails[id]
          if (detailsModule?.data) {
            Object.assign(detailsModule.data, patch)
          }
          const evCarInList = state.listModule.list.find(item => item.id === id)
          if (evCarInList) {
            Object.assign(evCarInList, patch)
          }
        })

        ElNotification({
          type: 'success',
          duration: 6000,
          message: userId
            ? 'Renter was assigned to the EV car.'
            : 'Renter assignment was removed.',
        })
      } catch (e) {
        const { error } = parseErrors(e)
        ElNotification({ message: error, duration: 10000, type: 'error' })
        throw e
      }
    },

    async updateEvCarOwner(id: string, ownerEvCar: string | null) {
      try {
        const { data: response } = await evCarsApi.updateEvCarOwner(
          id,
          ownerEvCar
        )

        const updated = response?.data
        if (!updated) {
          throw new Error('Failed to update owner')
        }

        // Only patch the owner_ev_car field to avoid overwriting other fields
        // with defaults from a partial response payload.
        const nextOwner = updated.owner_ev_car ?? ownerEvCar

        this.$patch((state: EvCarsState) => {
          const detailsModule = state.evCarsDetails[id]
          if (detailsModule?.data) {
            detailsModule.data.owner_ev_car = nextOwner
          }
          const evCarInList = state.listModule.list.find(item => item.id === id)
          if (evCarInList) {
            evCarInList.owner_ev_car = nextOwner
          }
        })

        ElNotification({
          type: 'success',
          duration: 6000,
          message: 'EV Car owner successfully updated.',
        })
      } catch (e) {
        const { error } = parseErrors(e)
        ElNotification({
          message: `Error updating owner: ${error}`,
          type: 'error',
          duration: 10000,
        })
        throw e
      }
    },

    async getEvVehicleTrips(
      vehicleId: string,
      params?: {
        page?: number
        perPage?: number
        sortBy?: string
        sortDirection?: 'asc' | 'desc'
      }
    ) {
      try {
        if (!this.evVehicleTrips[vehicleId]) {
          this.$patch({
            evVehicleTrips: {
              [vehicleId]: getInitialEvCarTripsState(),
            },
          })
        }

        this.$patch({
          evVehicleTrips: {
            [vehicleId]: { isLoading: true, error: null },
          },
        })

        const response = await evCarsApi.getEvVehicleTrips(vehicleId, params)

        const trips = response.data.data || []
        const meta = response.data.meta

        this.$patch({
          evVehicleTrips: {
            [vehicleId]: {
              isLoading: false,
              data: trips,
              meta,
            },
          },
        })
      } catch (e) {
        const { error } = parseErrors(e)
        console.error('❌ Error fetching vehicle trips:', error)
        this.$patch({
          evVehicleTrips: { [vehicleId]: { isLoading: false, error } },
        })
      }
    },
  },
})
